import {
  Box,
  Avatar,
  Button,
  Chip,
  Typography,
  Stack,
  Paper,
  CircularProgress,
} from "@mui/material";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import GridViewIcon from "@mui/icons-material/GridView";
import { useCallback, useEffect, useRef, useState } from "react";
import apiService from "../../../../../service/Api";
import { toast } from "react-toastify";
import { RootState } from "../../../../../store/Index";
import { useSelector } from "react-redux";
import { MemoryBook } from "../../../../../types/memory-table";
import { User } from "../../../../users-admin/types/User";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

interface Stream {
  id: number;
  title: string;
  status: string; // e.g., "Enquiry", potentially others
  priority?: string; // Optional
  author?: string; // Optional
  comments?: number; // Optional
  booking: {
    id: number;
    deceased: Array<{
      first_name: string;
      surname: string;
      person_image?: string;
    }>;
    serviceType?: string; // Optional, assumed from prior context
  };
  service: {
    date_of_service: string;
  };
  serviceType?: string; // Optional, may be at stream level
  start: string;
  end: string;
  date: string;
  address_one: string;
  address_two?: string;
  suburb: string;
  city: string;
  website_stream_link?: string | null;
  youtube_stream_link?: string | null;
}

interface MemorialMessage {
  id: number;
  stream: number;
  sender_full_name: string | null;
  sender_email: string | null;
  is_approved: boolean;
  approved_by: number | null;
  parent: number | null;
  content: string;
  created_at: string;
  updated_at: string;
}

interface Props {
  stream: Stream;
}
export const StreamTableRow: React.FC<Props> = ({ stream }) => {
  const navigate = useNavigate();
  const [allComments, setAllComments] = useState<number>(0);
  const [assignees, setAssignees] = useState<User[]>([]);
  const [memoryBook, setMemoryBook] = useState<MemoryBook | null>(null);
  const [loadingMemoryBook, setLoadingMemoryBook] = useState<boolean>(false);
  const [errorLoadingMemoryBook, setErrorLoadingMemoryBook] =
    useState<boolean>(false);
  const [isLoadingAssignees, setIsLoadingAssignees] = useState<boolean>(false);
  const [errorLoadingAssignees, setErrorLoadingAssignees] =
    useState<boolean>(false);
  const [approvedComments, setApprovedComments] = useState<number>(0);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const stackRef = useRef<HTMLDivElement | null>(null);
  const paperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const stackEl = stackRef.current;
    const paperEl = paperRef.current;

    if (!stackEl || !paperEl) return;

    const displayPaperView = () => {
      if (paperRef.current) {
        paperRef.current.style.display = "block";
      }
    };

    const hidePaperView = () => {
      if (paperRef.current) {
        paperRef.current.style.display = "none";
      }
    };

    stackEl.addEventListener("mouseenter", displayPaperView);
    stackEl.addEventListener("mouseleave", hidePaperView);

    return () => {
      stackEl.removeEventListener("mouseenter", displayPaperView);
      stackEl.removeEventListener("mouseleave", hidePaperView);
    };
  }, []);

  const coloredBarPercentage = (): string => {
    if (allComments === 0) {
      return "0%";
    }
    const percentage = Math.floor((approvedComments / allComments) * 100);
    return `${percentage}%`;
  };

  const fetchComments = useCallback(async () => {
    const id = stream.id;

    if (!id) {
      return;
    }

    try {
      const response = await apiService.get<{ results: MemorialMessage[] }>(
        `/api/chats/memorial-message/?stream_id=${id}`
      );
      const comments = response.data.results || [];
      const approvedComments: number = comments.filter(
        (comment) => comment.is_approved
      ).length;

      setAllComments(comments.length);
      setApprovedComments(approvedComments);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load comments. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    }
  }, [stream.id]);

  const getDate = (date: string): string => {
    const dateObj = new Date(date);

    // Format as dd/mm/yyyy
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0"); // months are 0-indexed
    const year = dateObj.getFullYear();

    const formattedDate = `${day}/${month}/${year}`;
    return formattedDate;
  };

  const getStatusChipProps = (status: boolean | undefined) => {
    if (status === undefined) {
      return {
        label: "Not available",
        bgcolor: "#eb354140",
        color: "#EB4335",
      };
    }
    switch (status) {
      case false:
        return {
          label: "In Progress",
          bgcolor: "#F79E1B40",
          color: "#F79E1B",
        };
      case true:
        return {
          label: "Confirmed",
          bgcolor: "#4BA66340",
          color: "#4BA663",
        };

      default:
        return {
          label: "Unknown",
          bgcolor: "#BDBDBD40",
          color: "#BDBDBD",
        };
    }
  };

  const fetchStreamMemoryBook = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null) => {
    if (e) {
      e.stopPropagation();
    }

    setLoadingMemoryBook(true);
    setErrorLoadingMemoryBook(false);
    try {
      const response = await apiService.get<{ results: MemoryBook[] }>(
        `/api/streams/memory-book/?stream=${stream.id}`
      );

      if (response.data.results.length !== 0) {
        setMemoryBook(response.data.results.at(-1) ?? null);
      } else {
        setMemoryBook(null);
      }
    } catch (error) {
      console.error(error);
      setErrorLoadingMemoryBook(true);
    } finally {
      setLoadingMemoryBook(false);
    }
  },
  [stream.id]
  );

  const getLastUpdatedBy = () => {
    if (memoryBook === null) {
      return "N/A";
    }

    if (
      memoryBook.last_updated_by.first_name.length === 0 ||
      memoryBook.last_updated_by.last_name.length === 0
    ) {
      return "N/A";
    }

    return memoryBook.last_updated_by.first_name.concat(
      memoryBook.last_updated_by.last_name
    );
  };

  const fetchMemoryBookAssignees = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null) => {
    if (e) {
      e.stopPropagation();
    }
    setIsLoadingAssignees(true);
    setErrorLoadingAssignees(false);

    try {
      const response = await apiService.get<{ data: { assignees: User[] } }>(
        `/api/streams/stream/${stream.id}/memory-book-assignees/`
      );

      setAssignees(response.data.data.assignees);
    } catch (error) {
      console.error(error);
      setErrorLoadingAssignees(true);
    } finally {
      setIsLoadingAssignees(false);
    }
  },
  [stream.id]
  );

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  useEffect(() => {
    fetchStreamMemoryBook(null);
  }, [fetchStreamMemoryBook]);

  useEffect(() => {
    fetchMemoryBookAssignees(null);
  }, [fetchMemoryBookAssignees]);

  const navigateToStreamDetails = () => {
    navigate(`/memory-table/view/${stream.id}`);
  };

  const openCanvaLink = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();
    if (!memoryBook?.canva_memory_book_link) {
      toast.warn("Canva link not available at the moment", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      return;
    }

    window.open(memoryBook?.canva_memory_book_link, "_blank");
  };

  /* const getRowBgcolor = () => {
    const incrementedIndex = index + 1;

    if (index === 0 || incrementedIndex % 2 === 0) {
      return reduxTheme.primary_colors[900];
    }

    if (incrementedIndex % 2 === 1) {
      return reduxTheme.primary_colors[800];
    }
  } */ return (
    <TableRow
      hover
      key={stream.id}
      sx={{
        "&:last-child td, &:last-child th": { border: 0 },
        padding: 50,
        cursor: "pointer",
      }}
      onClick={navigateToStreamDetails}
    >
      <TableCell>{stream.id}</TableCell>
      <TableCell>
        {stream.service ? getDate(stream.service.date_of_service) : "N/A"}
      </TableCell>
      <TableCell>
        {stream.booking.deceased.length > 0
          ? `${stream.booking.deceased[0].first_name} ${stream.booking.deceased[0].surname}`
          : "N/A"}
      </TableCell>
      <TableCell>
        <Typography variant="body1">
          {`${approvedComments}/${allComments}`}
        </Typography>
        <Box
          sx={{
            backgroundColor: "#F0F4F8",
            height: 10,
            width: "100%",
            borderRadius: 30,
            mt: 1,
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: coloredBarPercentage(),
              borderRadius: 30,
              bgcolor: reduxTheme.primary_colors[400],
            }}
          ></Box>
        </Box>
      </TableCell>
      <TableCell>
        {loadingMemoryBook && <CircularProgress size={25} />}
        {!loadingMemoryBook && errorLoadingMemoryBook && (
          <Button onClick={(e) => fetchStreamMemoryBook(e)}>Retry</Button>
        )}
        {!loadingMemoryBook && !errorLoadingMemoryBook && (
          <Chip
            label={getStatusChipProps(memoryBook?.is_delivered).label}
            sx={{
              bgcolor: getStatusChipProps(memoryBook?.is_delivered).bgcolor,
              color: getStatusChipProps(memoryBook?.is_delivered).color,
            }}
          />
        )}
      </TableCell>
      <TableCell>
        <Button
          onClick={(e) => openCanvaLink(e)}
          sx={{
            color: memoryBook?.canva_memory_book_link
              ? "#007AFF"
              : reduxTheme.primary_colors[300],
          }}
        >
          <GridViewIcon sx={{ mr: 1 }} /> Open link
        </Button>
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Avatar
            alt="user picture"
            sx={{ width: 25, height: 25 }}
            src="https://images.unsplash.com/photo-1463453091185-61582044d556?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
          ></Avatar>
          <Typography variant="body1" sx={{ ml: 1, fontSize: 13 }}>
            {getLastUpdatedBy()}
          </Typography>
        </Box>
      </TableCell>
      <TableCell sx={{ position: "relative" }}>
        {isLoadingAssignees && <CircularProgress size={25} />}

        {!isLoadingAssignees &&
          !errorLoadingAssignees &&
          assignees.length === 0 && <NoAssignees size={25} />}

        {errorLoadingAssignees && !isLoadingAssignees && (
          <Button onClick={(e) => fetchMemoryBookAssignees(e)}>Retry</Button>
        )}

        {!errorLoadingAssignees &&
          !isLoadingAssignees &&
          assignees.length > 0 && (
            <>
              <Stack
                ref={stackRef}
                direction={"row"}
                gap={"-2"}
                sx={{ cursor: "pointer" }}
              >
                {[1, 2, 3, 4].map((num, index) => {
                  return (
                    <Avatar
                      key={num}
                      alt="user picture"
                      sx={{
                        width: 25,
                        height: 25,
                        border: "1px solid #fff",
                        position: index > 0 ? "relative" : "static", // allowing avatars to overlay
                        left: index === 0 ? 0 : -10 * index, // allowing avatars to overlay
                      }}
                      src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                    ></Avatar>
                  );
                })}
              </Stack>
              <Paper
                ref={paperRef}
                style={{
                  width: "90%",
                  display: "none",
                  position: "absolute",
                  zIndex: 100,
                  padding: 5,
                }}
              >
                {[1, 2, 3, 4].map((num, index) => {
                  return (
                    <AssigneeDetails
                      key={index}
                      firstName="Jane"
                      lastName="Doe"
                      profileImg={null}
                    />
                  );
                })}
              </Paper>
            </>
          )}
      </TableCell>
    </TableRow>
  );
};

StreamTableRow.propTypes = {
  stream: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    booking: PropTypes.shape({
      id: PropTypes.number.isRequired,
      deceased: PropTypes.arrayOf(
        PropTypes.shape({
          first_name: PropTypes.string.isRequired,
          surname: PropTypes.string.isRequired,
          person_image: PropTypes.string,
        })
      ).isRequired,
      serviceType: PropTypes.string,
    }).isRequired,
    service: PropTypes.shape({
      date_of_service: PropTypes.string.isRequired,
    }).isRequired,
    start: PropTypes.string.isRequired,
    end: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    address_one: PropTypes.string.isRequired,
    address_two: PropTypes.string,
    suburb: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    website_stream_link: PropTypes.string,
    youtube_stream_link: PropTypes.string,
  }).isRequired,
};

interface NoAssigneesProps {
  size: number;
}

export const NoAssignees: React.FC<NoAssigneesProps> = ({ size }) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Avatar sx={{ width: size, height: size, bgcolor: "red" }}></Avatar>
      <Typography variant="body1">No assignees</Typography>
    </Box>
  );
};

NoAssignees.propTypes = {
  size: PropTypes.number.isRequired,
};

interface AssigneeDetailsProps {
  firstName: string;
  lastName: string;
  profileImg: string | null;
}

const AssigneeDetails: React.FC<AssigneeDetailsProps> = ({
  firstName,
  lastName,
  profileImg,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const defaultImg =
    "https://images.unsplash.com/photo-1463453091185-61582044d556?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        mb: 1,
        padding: 1,
        border: `1px solid ${reduxTheme.primary_colors[800]}`,
        borderRadius: 1,
      }}
    >
      <Avatar
        alt="user picture"
        sx={{ width: 25, height: 25 }}
        src={profileImg ? profileImg : defaultImg}
      ></Avatar>
      <Typography variant="body1" sx={{ ml: 1, fontSize: 13 }}>
        {`${firstName} ${lastName}`}
      </Typography>
    </Box>
  );
};

AssigneeDetails.propTypes = {
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  profileImg: PropTypes.string,
};
